import {
  DatabaseAuto,
  IcommerceUser,
  IcreateEcomerceOrder,
  IdataArrayResponse,
  IecommerceOrder,
  IfilterProps, IpaymentMeta, TupdateEcommerceOrder,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

type TecommerceOrder = IecommerceOrder<number, IcommerceUser>;

/**
 * Represents an e-commerce order in the system.
 * This class provides methods to manage and interact with e-commerce orders,
 * including creating, updating, and querying order information.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TecommerceOrder - The type definition for e-commerce order properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link EcommerceOrder} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const ecommerceOrder = await EcommerceOrder.getAll();
 *
 * const ecommerceOrder = await EcommerceOrder.filterAll({
 *
 * });
 *
 * const ecommerceOrder = await EcommerceOrder.bulkDelete({
 *
 * });
 *
 * const ecommerceOrder = await EcommerceOrder.getOne('resourceId');
 *
 * const ecommerceOrder = await EcommerceOrder.remove();
 *
 * ```
 */
export class EcommerceOrder extends DatabaseAuto {
  /** Base API endpoint for e-commerce order operations */
  private static readonly baseUrl = '/ecommerce-order';

  /** The scheduled delivery date for the order */
  readonly deliveryDate: TecommerceOrder['deliveryDate'];

  /** Optional inspection date for the order */
  readonly inspectionDate?: TecommerceOrder['inspectionDate'];

  /** Current status of the e-commerce order */
  readonly ecommerceOrderStatus: TecommerceOrder['ecommerceOrderStatus'];

  /** Unique reference ID for the order */
  readonly urId: TecommerceOrder['urId'];

  /** List of vendors associated with the order */
  readonly ecommerceVendors: TecommerceOrder['ecommerceVendors'];

  /** Date when the order was placed */
  readonly orderDate: TecommerceOrder['orderDate'];

  /** Billing address for the order */
  readonly billingAddress: TecommerceOrder['billingAddress'];

  /** Shipping address for the order */
  readonly shippingAddress: TecommerceOrder['shippingAddress'];

  /** Indicates if the order includes promotional items */
  readonly isPromo: TecommerceOrder['isPromo'];

  /** Indicates if the order includes special offers */
  readonly isOffer: TecommerceOrder['isOffer'];

  /** Cost of shipping for the order */
  readonly shippingCost: TecommerceOrder['shippingCost'];

  /** Payment method used for the order */
  readonly paymentMethod: TecommerceOrder['paymentMethod'];

  /** Delivery code for order tracking */
  readonly orderDeliveryCode: TecommerceOrder['orderDeliveryCode'];

  /** Metadata related to the payment */
  readonly paymentMeta: TecommerceOrder['paymentMeta'];

  /** Details of any fund transfers */
  readonly transferDetails: TecommerceOrder['transferDetails'];

  /** Details of any retry attempts */
  readonly retryDetails: TecommerceOrder['retryDetails'];

  /** Invoice information for the order */
  readonly invoice: TecommerceOrder['invoice'];

  /** Unique order number */
  readonly ecommerceOrderNumber: TecommerceOrder['ecommerceOrderNumber'];

  /** Information about the buyer */
  readonly buyer: TecommerceOrder['buyer'];

  /** Method used to place the order */
  readonly orderMethod: TecommerceOrder['orderMethod'];

  /**
   * Creates a new instance of EcommerceOrder.
   * @param {TecommerceOrder} data - The order data used to initialize the instance
   * @throws {Error} Throws an error if the data is invalid or missing required fields
   */
  constructor(data: TecommerceOrder) {
    const validOrderStatuses: TecommerceOrder['ecommerceOrderStatus'][] = [
      'pending',
      'processing',
      'shipped',
      'delivered',
      'completed',
      'cancelled',
      'refunded'
    ];

    validateData(data, {
      deliveryDate: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          const deliveryDate = v instanceof Date ? v : new Date(v);

          // Check date validity and ensure it's in the future
          return !isNaN(deliveryDate.getTime()) && deliveryDate > new Date();
        },
        message: 'Delivery date must be a valid date (Date object or ISO date string) in the future'
      },
      ecommerceOrderStatus: {
        validate: (v) =>
          typeof v === 'string' &&
          validOrderStatuses.includes(v),
        message: `EcommerceOrder status must be one of: ${validOrderStatuses.join(', ')}`
      },
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      billingAddress: {
        validate: (v) =>
          v !== null &&
          typeof v === 'object',
        message: 'Billing address must be a valid object'
      },
      shippingAddress: {
        validate: (v) =>
          v !== null &&
          typeof v === 'object',
        message: 'Shipping address must be a valid object'
      },
      shippingCost: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Shipping cost must be a non-negative number'
      },
      paymentMethod: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Payment method must be a non-empty string'
      }
    });

    super(data);

    this.deliveryDate = new Date(data.deliveryDate);
    if (data.inspectionDate) {
      this.inspectionDate = new Date(data.inspectionDate);
    }
    this.ecommerceOrderStatus = data.ecommerceOrderStatus;
    this.urId = data.urId;
    this.ecommerceVendors = data.ecommerceVendors;

    if (data.orderDate) {
      this.orderDate = new Date(data.orderDate);
    }

    this.billingAddress = data.billingAddress;
    this.shippingAddress = data.shippingAddress;
    this.isPromo = Boolean(data.isPromo);
    this.isOffer = Boolean(data.isOffer);
    this.shippingCost = data.shippingCost;
    this.paymentMethod = data.paymentMethod;
    this.orderDeliveryCode = data.orderDeliveryCode;
    this.paymentMeta = data.paymentMeta;
    this.transferDetails = data.transferDetails;
    this.retryDetails = data.retryDetails;
    this.invoice = data.invoice;
    this.ecommerceOrderNumber = data.ecommerceOrderNumber;
    this.buyer = data.buyer;
    this.orderMethod = data.orderMethod;
  }

  /**
   * Retrieves the current user's orders with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: EcommerceOrder[] }>}
   * An object containing the total count and an array of order records
   * @throws {Error} If there's an error fetching the orders
   */
  static async getMyOrders(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TecommerceOrder>>(`${EcommerceOrder.baseUrl}/get-my-orders?${query}`);
    const orders = await lastValueFrom(observer$);

    return {
      count: orders.count,
      dataArr: orders.data.map(val => new EcommerceOrder(val))
    };
  }

  /**
   * Retrieves a specific order for the currently authenticated user.
   * @param {string} id - The order ID to retrieve
   * @returns {Promise<EcommerceOrder>} The retrieved order
   * @throws {Error} If the order is not found or an error occurs during retrieval
   */
  static async getMyOrder(id: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TecommerceOrder>(`${EcommerceOrder.baseUrl}/get-my-order/${id}`);
    const order = await lastValueFrom(observer$);

    return new EcommerceOrder(order);
  }

  /**
   * Retrieves all e-commerce orders with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: EcommerceOrder[] }>}
   * An object containing the total count and an array of order records
   * @throws {Error} If there's an error fetching the orders
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TecommerceOrder>>(`${EcommerceOrder.baseUrl}?${query}`);
    const orders = await lastValueFrom(observer$);

    return {
      count: orders.count,
      dataArr: orders.data.map(val => new EcommerceOrder(val))
    };
  }

  /**
   * Filters e-commerce orders based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: EcommerceOrder[] }>}
   * An object containing the total count and an array of filtered order records
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TecommerceOrder>>(`${EcommerceOrder.baseUrl}/filter`, filter);
    const orders = await lastValueFrom(observer$);

    return {
      count: orders.count,
      dataArr: orders.data.map(val => new EcommerceOrder(val))
    };
  }

  /**
   * Retrieves a single e-commerce order by its ID.
   * @param {string} resourceId - The unique identifier of the order to retrieve
   * @returns {Promise<EcommerceOrder>} The requested order record
   * @throws {Error} If the order is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TecommerceOrder>(`${EcommerceOrder.baseUrl}/${resourceId}`);
    const order = await lastValueFrom(observer$);

    return new EcommerceOrder(order);
  }

  /**
   * Creates a new e-commerce order.
   * @param {IcreateEcomerceOrder} payload - The order data to create
   * @returns {Promise<{ ecommerceOrder: EcommerceOrder; paymentMeta?: IpaymentMeta; receiptMeta?: { _id: string } }>}
   * An object containing the created order and optional payment/receipt metadata
   * @throws {Error} If the order creation fails
   */
  static async add(payload: IcreateEcomerceOrder) {
    const observer$ = CommerceClient.ehttp
      .makePost<{
        ecommerceOrder: TecommerceOrder;
        paymentMeta?: IpaymentMeta;
        receiptMeta?: {
          _id: string;
        };
      }>(`${EcommerceOrder.baseUrl}`, payload);

    const res = await lastValueFrom(observer$);

    return {
      ecommerceOrder: new EcommerceOrder(res.ecommerceOrder),
      paymentMeta: res.paymentMeta,
      receiptMeta: res.receiptMeta
    };
  }

  /**
   * Updates the current e-commerce order with new values.
   * @param {TupdateEcommerceOrder} payload - The data to update the order with
   * @returns {Promise<{ ecommerceOrder: EcommerceOrder; receiptMeta?: { _id: string } }>}
   * An object containing the updated order and optional receipt metadata
   * @throws {Error} If the update operation fails
   */
  async update(payload: TupdateEcommerceOrder) {
    const observer$ = CommerceClient.ehttp
      .makePut<{
        ecommerceOrder: TecommerceOrder;
        receiptMeta?: {
          _id: string;
        };
      }>(`${EcommerceOrder.baseUrl}/${this._id}`, payload);

    const res = await lastValueFrom(observer$);

    return {
      ecommerceOrder: new EcommerceOrder(res.ecommerceOrder),
      receiptMeta: res.receiptMeta
    };
  }
}
